import React, { useState, useRef, useEffect } from "react";
import { Box, Menu, Button } from "@mui/material";
import {
  allCustomFieldsTypes,
  isFirstTimeVisit,
} from "../../../../static/staticdata";
import { useDispatch, useSelector } from "react-redux";
import { getPracticeArea } from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../../static/server";
import { FormProvider, useForm } from "react-hook-form";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import FormBuilder from "./components/FormBuilder";
import SectionMenu from "./components/SectionMenu";
import FieldMenu from "./components/FieldMenu";
import { toast } from "react-toastify";
import { useTour } from "../../../ToureStepps/TourProvider";
import { CreatecaseTemplateFormSteps } from "../../../ToureStepps/TourStepsData";
import defaultSections from "./components/defaultSections";
import { useDialog } from "../../../DialogContext";
import DeleteWarningDialog from "./components/DeleteWarningDialog";
import axios from "axios";
import {
  resolveMatterTemplateFieldData,
  resolveMatterTemplateSections,
} from "../../../../utils/matterTemplateConfig";
import { appendTeamMemberLawyerId } from "../../../../styles/styles";
const toSnakeCase = (str) => {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/gi, "")
    .replace(/\s+/g, "_");
};

const generateUniqueName = (label, sections, currentFieldId = null) => {
  let baseName = toSnakeCase(label);
  let uniqueName = baseName;
  let counter = 1;

  const isNameUnique = () => {
    return !sections.some((section) =>
      section.fields.some(
        (field) => field.name === uniqueName && field.id !== currentFieldId
      )
    );
  };

  while (!isNameUnique()) {
    uniqueName = `${baseName}_${counter}`;
    counter++;
  }
  return uniqueName;
};

const getInitialSections = (data) => {
  const rawSections = resolveMatterTemplateSections(data, "createNewMatter");
  return rawSections.map((sec, idx) => ({
    id: sec.id || sec.section_name || `section_${idx + 1}`,
    name: sec.name || sec.section_name || `section_${idx + 1}`,
    section_name: sec.section_name || sec.name || `section_${idx + 1}`,
    ...sec,
  }));
};

const getMaxSectionIndex = (sections) => {
  const numbers = sections
    .map((s) => {
      if (typeof s.id === "string") {
        const match = s.id.match(/(\d+)/);
        if (match) return parseInt(match[1]);
      }
      if (typeof s.id === "number") return s.id;
      return NaN;
    })
    .filter((n) => !isNaN(n));
  return numbers.length ? Math.max(...numbers) : sections.length;
};

const ensureSystemSections = (sections = []) => {
  const result = [...sections];
  defaultSections.forEach((def) => {
    const defName = (def.name || def.section_name || "").toLowerCase();
    if (
      !result.some((s) => {
        const secName = (s.name || s.section_name || "").toLowerCase();
        return secName === defName;
      })
    ) {
      result.push(JSON.parse(JSON.stringify(def)));
    }
  });
  return result;
};

const CreatecaseTemplateForm = ({ editTempData, templates = [] }) => {
  const isEditMode = Boolean(editTempData && editTempData.id);
  const scrollContainerRef = useRef(null);
  const scrollState = useRef({ direction: null, animationFrameId: null });
  const { allPracticeArea } = useSelector((state) => state.case);
  const { token, lawerId, teamMemberLawyerId } = useSelector((data) => data.user);
  const dispatch = useDispatch();
  const draggedItemRef = useRef(null);
  const [customFieldsExpanded, setCustomFieldsExpanded] = useState(true);
  const [unUsedExpanded, setUnUsedExpanded] = useState(false);
  const [usedExpanded, setUsedExpanded] = useState(false);
  const [tabBtn, setTabBtn] = useState(1);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [sections, setSections] = useState(
    ensureSystemSections(getInitialSections(editTempData) || defaultSections)
  ); // main state to show case template sections
  const [sectionMenuOpen, setSectionMenuOpen] = useState(null);
  const [sectionMenuOpenId, setSectionMenuOpenId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTarget, setDragTarget] = useState({
    type: "",
    sectionId: null,
    fieldIndex: null,
    isMovingSection: false,
    isMovingField: false,
    sourceId: null,
  });
  const [fieldCounter, setFieldCounter] = useState({});
  const [sectionCounter, setSectionCounter] = useState(
    getMaxSectionIndex(getInitialSections(editTempData))
  );
  const [activeSectionDropIndex, setActiveSectionDropIndex] = useState(null);
  const [activeFieldDropData, setActiveFieldDropData] = useState({
    sectionId: null,
    fieldIndex: null,
  });
  const [activeColumn, setActiveColumn] = useState(3);
  const [activeAlign, setActiveAlign] = useState("top_to_bottom");
  const [linkId, setLinkId] = useState(
    editTempData?.linked_templated ?? editTempData?.id
  );
  const [templateName, setTemplateName] = useState(
    editTempData?.template_name ?? "Untitled Template Name"
  );
  const [practiceAreaId, setPracticeAreaId] = useState(
    editTempData?.practice_area?.id ?? ""
  );
  const [tempDescription, setTempDescription] = useState(
    editTempData?.template_description
  );
  const initialFieldData = resolveMatterTemplateFieldData(editTempData);
  const [formType, setFormType] = useState("createNewMatter");
  const [unUsedFieldsTypes, setUnUsedFieldsTypes] = useState(
    initialFieldData?.unUsedFieldsTypes || []
  );
  const [usedFieldsTypes, setUsedFieldsTypes] = useState(
    initialFieldData?.usedFieldsTypes || []
  );
  const [defaultSectionFields, setDefaultSectionFields] = useState([]);
  const [defaultSectionId] = useState("default-section");
  const [usedInDefaultSection, setUsedInDefaultSection] = useState(new Set());
  const { showDialog, closeDialog } = useDialog();
  const [copyTemplateId, setCopyTemplateId] = useState("");
  const [templateOptions, setTemplateOptions] = useState(templates);
  const SCROLL_THRESHOLD = 20;

  useEffect(() => {
    if (isFirstTimeVisit("create_case_template_form")) {
      handleStartTour();
    }
  }, []);

  const createNewMatterMethods = useForm({
    defaultValues: {
      form_name: "Create New Matter",
      sections: [],
    },
  });

  const quickCreateNewMatterMethods = useForm({
    defaultValues: {
      form_name: "Quick Create New Matter",
      sections: [],
    },
  });

  const currentMethods =
    formType === "createNewMatter"
      ? createNewMatterMethods
      : quickCreateNewMatterMethods;

  const handleCopyFromTemplate = async (templateId) => {
    if (!templateId) return;
    const ok = await confirmOverwrite(
      "Loading another template will replace all current sections. Continue?"
    );
    if (!ok) return;
    try {
      const res = await axios.get(
        appendTeamMemberLawyerId(
          `${backend_api}case/template-register/${templateId}/`,
          teamMemberLawyerId,
        ),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      );
      const data = res.data;
      const resolvedFieldData = resolveMatterTemplateFieldData(data);
      setPracticeAreaId(data.practice_area?.id || "");
      setSections(
        ensureSystemSections(getInitialSections(data) || defaultSections)
      );
      setUnUsedFieldsTypes(resolvedFieldData?.unUsedFieldsTypes || []);
      setUsedFieldsTypes(resolvedFieldData?.usedFieldsTypes || []);
      setSectionCounter(getMaxSectionIndex(getInitialSections(data)));
      setLinkId(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load template data");
    }
  };

  const confirmDelete = (msg) => {
    if (sessionStorage.getItem("hideTemplateDeleteWarning") === "true") {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      const dialogId = showDialog(
        <DeleteWarningDialog
          message={msg}
          primaryBtnText="Delete"
          btnColor="error"
          onConfirm={() => {
            closeDialog(dialogId);
            resolve(true);
          }}
          onCancel={() => {
            closeDialog(dialogId);
            resolve(false);
          }}
        />,
        { dialogTitle: "Delete field", width: "40%", deleteType: true }
      );
    });
  };

  const confirmOverwrite = (msg) => {
    return new Promise((resolve) => {
      const dialogId = showDialog(
        <DeleteWarningDialog
          message={msg}
          primaryBtnText="Continue"
          btnColor="warning"
          onConfirm={() => {
            closeDialog(dialogId);
            resolve(true);
          }}
          onCancel={() => {
            closeDialog(dialogId);
            resolve(false);
          }}
          confirmText="Continue"
          confirmColor="primary"
          showCheckbox={false}
        />,
        { dialogTitle: "Load Template", warningType: true, width: "40%" }
      );
    });
  };

  useEffect(() => {
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      })
    );
  }, [dispatch, token]);
  useEffect(() => {
    if (isEditMode) return;
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(
          appendTeamMemberLawyerId(
            `${backend_api}case/template-register/`,
            teamMemberLawyerId,
          ),
          {
          headers: { Authorization: `Token ${token}` },
          },
        );
        setTemplateOptions(res.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    if (templateOptions.length === 0) {
      fetchTemplates();
    }
  }, [isEditMode, teamMemberLawyerId, token, templateOptions.length]);

  useEffect(() => {
    const defaultSection = sections.find(
      (section) => section.id === defaultSectionId
    );
    if (defaultSection) {
      setDefaultSectionFields(defaultSection.fields);
    }
  }, [sections, defaultSectionId]);

  const toggleCustomFields = () => {
    setCustomFieldsExpanded((prev) => !prev);
    setUnUsedExpanded(false);
  };

  const toggleUnusedFields = () => {
    setUnUsedExpanded((prev) => !prev);
    setCustomFieldsExpanded(false);
  };

  const toggleUsedFields = () => {
    setUsedExpanded((prev) => !prev);
  };

  const handleTabChange = (tabId) => {
    setTabBtn(tabId);
    setFormType(tabId === 1 ? "createNewMatter" : "quickCreateNewMatter");

    const existingDefaultSection = sections.find(
      (section) => section.id === defaultSectionId
    );

    if (tabId === 2) {
      if (!existingDefaultSection) {
        const defaultSection = {
          id: defaultSectionId,
          name: "Default Section",
          columns: 1,
          alignment: "top_to_bottom",
          fields: defaultSectionFields,
        };
        setSections((prev) => [...prev, defaultSection]);
      }
    } else {
      if (!existingDefaultSection && defaultSectionFields.length > 0) {
        const defaultSection = {
          id: defaultSectionId,
          name: "Default Section",
          columns: 3,
          alignment: "top_to_bottom",
          fields: defaultSectionFields,
        };
        setSections((prev) => [...prev, defaultSection]);
      }
    }

    // Update used fields types based on current default section fields
    const defaultSection = sections.find(
      (section) => section.id === defaultSectionId
    );
    if (defaultSection) {
      const defaultSectionFieldLabels = defaultSection.fields.map(
        (field) => field.label || field.name
      );
      setUsedFieldsTypes((prev) =>
        prev.map((field) => ({
          ...field,
          disabled: defaultSectionFieldLabels.includes(
            field.label || field.name
          ),
        }))
      );
    }
  };

  const handleDragStart = (e, fieldType) => {
    // Check if field is already in default section
    if (usedInDefaultSection.has(fieldType.isUsed && fieldType.value)) {
      e.preventDefault();
      toast.info("This field is already in the Quick Create form");
      return;
    }

    setIsDragging(true);

    if (fieldType.isRemoved) {
      draggedItemRef.current = { ...fieldType, isNew: false, type: "field" };
      e.dataTransfer.setData(
        "text/plain",
        JSON.stringify({
          type: "field",
          value: fieldType.value,
          isNew: false,
          id: fieldType.id,
          isRemoved: true,
        })
      );
    } else {
      draggedItemRef.current = { ...fieldType, isNew: true, type: "field" };
      e.dataTransfer.setData(
        "text/plain",
        JSON.stringify({
          type: "field",
          value: fieldType.value,
          isNew: true,
        })
      );
    }

    e.dataTransfer.effectAllowed = "copy";
  };
  const handleFieldDragStart = (e, field, sourceSectionId, index) => {
    e.stopPropagation();
    setIsDragging(true);

    const fieldData = {
      // Create clean field data without undefined properties
      ...field,
      isNew: false,
      sourceSectionId,
      sourceIndex: index,
      type: "field",
      id: field.id,
    };

    draggedItemRef.current = fieldData;

    try {
      e.dataTransfer.setData(
        "text/plain",
        JSON.stringify({
          type: "field",
          fieldId: field.id,
          sourceSectionId: sourceSectionId,
          sourceIndex: index,
          isNew: false,
          value: field.value,
        })
      );
    } catch (error) {
      console.error("DataTransfer setData failed:", error);
    }

    setDragTarget({
      type: "field",
      isMovingField: true,
      sourceId: sourceSectionId,
      sourceIndex: index,
    });

    e.dataTransfer.effectAllowed = "move";
  };

  // both type section handle here new section and old section
  const handleSectionDragStart = (e, sectionId) => {
    setIsDragging(true);
    const isMovingSection = sectionId !== null && sectionId !== undefined;
    draggedItemRef.current = {
      type: "section",
      isNew: !isMovingSection,
      sectionId,
    };
    try {
      e.dataTransfer.setData(
        "text/plain",
        JSON.stringify({
          type: "section",
          isNew: !isMovingSection,
          sectionId,
        })
      );
    } catch (error) {
      console.error("DataTransfer setData failed:", error);
    }

    if (isMovingSection) {
      setDragTarget({
        type: "section",
        isMovingSection: true,
        sourceId: sectionId,
      });
    }
    e.dataTransfer.effectAllowed = isMovingSection ? "move" : "copy";
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    draggedItemRef.current = null;
    setDragTarget({
      type: null,
      sectionId: null,
      fieldIndex: null,
      isMovingSection: false,
      isMovingField: false,
      sourceId: null,
    });

    setActiveSectionDropIndex(null);
    setActiveFieldDropData({
      sectionId: null,
      fieldIndex: null,
    });
    stopSmoothScroll();
  };
  const handleDragEnter = (e, type, sectionId = null, fieldIndex = null) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedItemRef.current) return;
    if (draggedItemRef.current.type === "section" && type !== "section") return;
    if (draggedItemRef.current.type === "field" && type !== "field") return;

    setDragTarget({
      ...dragTarget,
      type,
      sectionId,
      fieldIndex,
    });
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragTarget({
        ...dragTarget,
        sectionId: dragTarget.type === "section" ? dragTarget.sectionId : null,
        fieldIndex: null,
      });
    }
  };

  const startSmoothScroll = (direction) => {
    const box = scrollContainerRef.current;
    if (!box) return;

    const scroll = () => {
      if (direction === "up") {
        box.scrollTop -= 5;
      } else if (direction === "down") {
        box.scrollTop += 5;
      }

      scrollState.current.animationFrameId = requestAnimationFrame(scroll);
    };

    if (scrollState.current.direction !== direction) {
      stopSmoothScroll();
      scrollState.current.direction = direction;
      scroll();
    }
  };

  const stopSmoothScroll = () => {
    if (scrollState.current.animationFrameId) {
      cancelAnimationFrame(scrollState.current.animationFrameId);
      scrollState.current.animationFrameId = null;
      scrollState.current.direction = null;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();

    e.dataTransfer.dropEffect =
      dragTarget.isMovingSection || dragTarget.isMovingField ? "move" : "copy";

    const box = scrollContainerRef.current;
    if (!box) return;

    const rect = box.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;

    if (offsetY < SCROLL_THRESHOLD) {
      startSmoothScroll("up");
    } else if (offsetY > rect.height - SCROLL_THRESHOLD) {
      startSmoothScroll("down");
    } else {
      stopSmoothScroll(); // Stop if not near edge
    }
  };

  const handleSectionReorderDrop = (e, targetPosition) => {
    e.preventDefault();
    setIsDragging(false);
    const draggedItem = draggedItemRef.current;
    if (!draggedItem || draggedItem.type !== "section") return;

    if (draggedItem.isNew) {
      // Create new section
      const newSectionCounter = sectionCounter + 1;
      setSectionCounter(newSectionCounter);

      const newSection = {
        id: `section_${newSectionCounter}`,
        name: `Section ${newSectionCounter}`,
        columns: 3,
        alignment: "top_to_bottom",
        fields: [],
      };

      const newSections = [...sections];
      newSections.splice(targetPosition, 0, newSection);
      setSections(newSections);
    } else if (draggedItem.sectionId) {
      // Reorder existing section
      const sourceSectionIndex = sections.findIndex(
        (s) => s.id === draggedItem.sectionId
      );
      if (sourceSectionIndex === -1) return;

      const newSections = [...sections];
      const [movedSection] = newSections.splice(sourceSectionIndex, 1);

      let insertAt = targetPosition;
      if (sourceSectionIndex < targetPosition) {
        insertAt--;
      }
      insertAt = Math.max(0, Math.min(insertAt, newSections.length));

      newSections.splice(insertAt, 0, movedSection);
      setSections(newSections);
    }

    setDragTarget({
      type: null,
      sectionId: null,
      fieldIndex: null,
      isMovingSection: false,
      isMovingField: false,
      sourceId: null,
    });
    draggedItemRef.current = null;
  };

  const handleSectionDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const draggedItem = draggedItemRef.current;
    if (!draggedItem || draggedItem.type !== "section" || !draggedItem.isNew) {
      return; // Not a section or not a new section
    }

    const newSectionCounter = sectionCounter + 1;
    setSectionCounter(newSectionCounter);

    const newSection = {
      id: `section_${newSectionCounter}`,
      name: `Section ${newSectionCounter}`,
      columns: 3,
      alignment: "top_to_bottom",
      fields: [],
    };

    setSections((prev) => [...prev, newSection]);

    setDragTarget({
      type: null,
      sectionId: null,
      fieldIndex: null,
      isMovingSection: false,
      isMovingField: false,
      sourceId: null,
    });
    stopSmoothScroll();
    draggedItemRef.current = null;
  };

  const handleDefaultSectionFieldDrop = (e, targetSectionId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItemRef.current) return;

    if (draggedItemRef.current.type === "section") return;

    try {
      const fieldType = draggedItemRef.current.value;

      setFieldCounter((prev) => ({
        ...prev,
      }));

      // Create new field preserving original properties
      const newField = {
        ...draggedItemRef.current,
        // id: `${fieldType}_${newCount}`,
        // Keep the original label if it exists, otherwise use the name
        label: draggedItemRef.current.label || draggedItemRef.current.name,
        name: generateUniqueName(
          draggedItemRef.current.label || draggedItemRef.current.name,
          sections
        ),
        required: false, // Preserve original required status
        value: fieldType,
        disabled: true, // Disable field in default section
        icons: draggedItemRef.current.icons || draggedItemRef.current.icon,
        isUsed: true,
        isRemoved: false,
      };

      // Initialize pickList for pick_list type fields
      if (fieldType === "pick_list") {
        newField.pickList = {
          options: [
            { label: "Option 1", value: "option_1" },
            { label: "Option 2", value: "option_2" },
          ],
          multiSelect: false,
          defaultValue: "",
          useColors: false,
        };
      }

      // Add to sections
      setSections((prevSections) =>
        prevSections.map((section) => {
          if (section && section.id === targetSectionId) {
            const newFields = [...(section.fields || [])];
            newFields.unshift(newField);
            return { ...section, fields: newFields };
          }
          return section;
        })
      );

      // Add to defaultSectionFields
      setDefaultSectionFields((prev) => {
        const newFields = [...prev];
        newFields.unshift(newField);
        return newFields;
      });

      // Mark field as used in default section
      setUsedInDefaultSection((prev) => new Set([...prev, fieldType]));

      // Update usedFieldsTypes in sidebar
      setUsedFieldsTypes((prev) => {
        const existingFieldIndex = prev.findIndex(
          (field) => field.value === fieldType
        );

        if (existingFieldIndex !== -1) {
          const updatedFields = [...prev];
          updatedFields[existingFieldIndex] = {
            ...updatedFields[existingFieldIndex],
            disabled: true,
            isUsed: true,
            isRemoved: false,
          };
          return updatedFields;
        } else {
          return [...prev, newField];
        }
      });

      // Remove from unUsedFieldsTypes if present
      setUnUsedFieldsTypes((prev) =>
        prev.filter((field) => field.value !== fieldType)
      );

      toast.success("Field added to Quick Create form");
    } catch (error) {
      console.error("Error creating field in default section:", error);
      toast.error("Failed to add field to Quick Create form");
    }

    setDragTarget({
      type: null,
      sectionId: null,
      fieldIndex: null,
      isMovingSection: false,
      isMovingField: false,
      sourceId: null,
    });
    draggedItemRef.current = null;
  };

  const handleFieldDrop = (e, targetSectionId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItemRef.current) return;

    const isDefaultSection = targetSectionId === defaultSectionId;
    if (isDefaultSection) {
      handleDefaultSectionFieldDrop(e, targetSectionId);
      return;
    }

    try {
      // Safely access properties with null checks
      const isFromUnusedFields =
        draggedItemRef.current && draggedItemRef.current.isRemoved === true;

      // Handle fields from unused fields right away
      if (isFromUnusedFields && draggedItemRef.current) {
        const draggedId = draggedItemRef.current.id;

        const newField = {
          ...draggedItemRef.current,
          isRemoved: false,
          isNew: false,
        };

        // Add to section with position_index
        setSections((prevSections) =>
          prevSections.map((section) => {
            if (section.id !== targetSectionId) return section;

            // Calculate the new position_index
            const position_index = section.fields ? section.fields.length : 0;

            return {
              ...section,
              fields: [
                ...(section.fields || []),
                { ...newField, position_index },
              ],
            };
          })
        );

        // Only remove dragged field safely
        setUnUsedFieldsTypes((prev) =>
          Array.isArray(prev) ? prev.filter((f) => f?.id !== draggedId) : []
        );

        // Add to used fields
        setUsedFieldsTypes((prev) => [
          ...(Array.isArray(prev) ? prev : []),
          newField,
        ]);

        // Cleanup
        draggedItemRef.current = null;
        setDragTarget({
          type: null,
          sectionId: null,
          fieldIndex: null,
          isMovingSection: false,
          isMovingField: false,
          sourceId: null,
        });

        return;
      }

      // Check if we're not dealing with a section
      if (draggedItemRef.current && draggedItemRef.current.type === "section")
        return;

      // Handle moving existing field between sections
      if (draggedItemRef.current && !draggedItemRef.current.isNew) {
        const sourceSectionId = draggedItemRef.current.sourceSectionId;
        const sourceIndex = draggedItemRef.current.sourceIndex;

        // Skip processing if we don't have valid source info
        if (sourceSectionId === null || sourceIndex === null) {
          toast.info("Invalid source information for existing field");
          return;
        }

        // Find source section with safety checks
        const sourceSection = sections.find(
          (s) => isValidObject(s) && s.id === sourceSectionId
        );

        if (!sourceSection) {
          console.error("Source section not found");
          return;
        }

        // Safely check fields array
        if (!Array.isArray(sourceSection.fields)) {
          console.error("Source section fields is not an array");
          return;
        }

        // Find field to move with bounds check
        if (sourceIndex < 0 || sourceIndex >= sourceSection.fields.length) {
          console.error("Invalid source index");
          return;
        }

        const fieldToMove = sourceSection.fields[sourceIndex];
        if (!fieldToMove) {
          console.error("Field to move not found");
          return;
        }

        // Create a copy of the field with a new ID
        const newField = {
          ...fieldToMove,
          sourceSectionId: null,
          sourceIndex: null,
        };

        // Update sections state with thorough null checks
        setSections((prevSections) => {
          if (!Array.isArray(prevSections)) return prevSections;

          return prevSections.map((section) => {
            if (!section) return section;

            if (section.id === sourceSectionId) {
              // Remove field from source section
              const filteredFields = Array.isArray(section.fields)
                ? section.fields.filter((_, index) => index !== sourceIndex)
                : [];

              const reindexedFields = filteredFields.map((field, idx) => ({
                ...field,
                position_index: idx,
              }));

              return {
                ...section,
                fields: reindexedFields,
              };
            }

            if (section.id === targetSectionId) {
              // Add field to target section with new position_index
              const updatedFields = Array.isArray(section.fields)
                ? [
                    ...section.fields,
                    { ...newField, position_index: section.fields.length },
                  ]
                : [{ ...newField, position_index: 0 }];

              return {
                ...section,
                fields: updatedFields,
              };
            }

            return section;
          });
        });

        // Update usedFieldsTypes
        setUsedFieldsTypes((prev) => {
          if (!Array.isArray(prev)) return [newField];
          return prev.map((field) => {
            if (!field) return field;
            return field.id === fieldToMove.id ? newField : field;
          });
        });
      } else if (draggedItemRef.current) {
        // Handle new field from sidebar
        const fieldType = draggedItemRef.current.value || "unknown";
        const currentCount = fieldCounter[fieldType] || 0;
        const newCount = currentCount + 1;

        setFieldCounter((prev) => ({
          ...prev,
          [fieldType]: newCount,
        }));

        // Create new field
        const newField = {
          ...draggedItemRef.current,
          id: `${fieldType}_${newCount}`,
          label: `${
            draggedItemRef.current.defaultLabel ||
            draggedItemRef.current.name ||
            "Field"
          } ${newCount}`,
          name: generateUniqueName(
            `${
              draggedItemRef.current.defaultLabel ||
              draggedItemRef.current.name ||
              "Field"
            } ${newCount}`,
            sections
          ),
          required: false,
          value: fieldType,
          field_type: fieldType, // Add field_type for consistency
          icons: draggedItemRef.current.icons || draggedItemRef.current.icon,
        };

        // Initialize pickList for pick_list type fields
        if (fieldType === "pick_list") {
          newField.pickList = {
            options: [
              { label: "Option 1", value: "option_1" },
              { label: "Option 2", value: "option_2" },
            ],
            multiSelect: false,
            defaultValue: "",
            useColors: false,
          };
        }

        // Add to sections with null checks
        setSections((prevSections) => {
          if (!Array.isArray(prevSections)) return prevSections;

          return prevSections.map((section) => {
            if (!section) return section;

            if (section.id === targetSectionId) {
              const fields = Array.isArray(section.fields)
                ? section.fields
                : [];
              const position_index = fields.length;

              return {
                ...section,
                fields: [...fields, { ...newField, position_index }],
              };
            }
            return section;
          });
        });

        // Add to usedFieldsTypes if not already present
        setUsedFieldsTypes((prev) => {
          if (!Array.isArray(prev)) return [newField];

          const alreadyExists = prev.some(
            (field) => field && newField && field.id === newField.id
          );

          return alreadyExists ? prev : [...prev, newField];
        });
      }
    } catch (error) {
      console.error("Error in handleFieldDrop:", error);
    } finally {
      setDragTarget({
        type: null,
        sectionId: null,
        fieldIndex: null,
        isMovingSection: false,
        isMovingField: false,
        sourceId: null,
      });
      draggedItemRef.current = null;
    }
  };
  const handleFieldReorderDrop = (e, targetSectionId, targetIndex) => {
    e.preventDefault();
    setIsDragging(false);

    try {
      const draggedItem = draggedItemRef.current;
      if (!isValidObject(draggedItem)) {
        console.error("Invalid dragged item");
        return;
      }

      if (draggedItem.type !== "field") {
        // console.error("Dragged item is not a field");
        toast.error("Dragged item is not a field");
        return;
      }

      if (draggedItem.isNew) {
        handleFieldDrop(e, targetSectionId);
        return;
      }

      const sourceSectionId = draggedItem.sourceSectionId;
      const sourceIndex = draggedItem.sourceIndex;

      // Find the section and field data
      const sourceSection = sections.find(
        (s) => isValidObject(s) && s.id === sourceSectionId
      );

      if (!isValidObject(sourceSection)) {
        console.error("Source section not found", sourceSectionId);
        return;
      }

      if (!Array.isArray(sourceSection.fields)) {
        console.error("Source section fields is not an array", sourceSection);
        return;
      }

      // Check if sourceIndex is valid
      if (sourceIndex < 0 || sourceIndex >= sourceSection.fields.length) {
        console.error("Invalid source index", {
          sourceIndex,
          fieldsLength: sourceSection.fields.length,
        });
        return;
      }

      const fieldToMove = sourceSection.fields[sourceIndex];
      if (!isValidObject(fieldToMove)) {
        console.error("Field to move is invalid", fieldToMove);
        return;
      }

      const fieldCopy = { ...fieldToMove };

      if (sourceSectionId === targetSectionId) {
        if (targetIndex === sourceIndex || targetIndex === sourceIndex + 1) {
          setDragTarget({
            type: null,
            sectionId: null,
            fieldIndex: null,
            isMovingSection: false,
            isMovingField: false,
            sourceId: null,
          });
          draggedItemRef.current = null;
          return;
        }

        setSections((prev) =>
          prev.map((section) => {
            if (!isValidObject(section)) return section;
            if (section.id === targetSectionId) {
              const newFields = [...section.fields].filter((field) =>
                isValidObject(field)
              );
              const [movedField] = newFields.splice(sourceIndex, 1);
              const adjustedTargetIndex =
                sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
              newFields.splice(adjustedTargetIndex, 0, movedField);

              const newFieldsWithPositions = newFields.map((field, idx) => ({
                ...field,
                position_index: idx,
              }));

              return { ...section, fields: newFieldsWithPositions };
            }
            return section;
          })
        );
      } else {
        setSections((prev) =>
          prev.map((section) => {
            if (!isValidObject(section)) return section;

            if (section.id === sourceSectionId) {
              // Remove field from source section
              const newSourceFields = [...section.fields].filter((field) =>
                isValidObject(field)
              );
              newSourceFields.splice(sourceIndex, 1);

              // Update position indices
              const updatedSourceFields = newSourceFields.map((field, idx) => ({
                ...field,
                position_index: idx,
              }));

              return {
                ...section,
                fields: updatedSourceFields,
              };
            }

            if (section.id === targetSectionId) {
              // Add field to target section
              const newTargetFields = [...section.fields].filter((field) =>
                isValidObject(field)
              );

              const newField = {
                ...fieldCopy,
                id: `${fieldCopy.value}_${newTargetFields.length + 1}`,
                name: generateUniqueName(fieldCopy.label, prev),
              };

              const validTargetIndex = Math.max(
                0,
                Math.min(targetIndex, newTargetFields.length)
              );

              newTargetFields.splice(validTargetIndex, 0, newField);

              // Update position indices
              const updatedTargetFields = newTargetFields.map((field, idx) => ({
                ...field,
                position_index: idx,
              }));

              return {
                ...section,
                fields: updatedTargetFields,
              };
            }

            return section;
          })
        );
        // "Field movement completed successfully");
      }
    } catch (error) {
      console.error("Error in handleFieldReorderDrop:", error);
    } finally {
      setDragTarget({
        type: null,
        sectionId: null,
        fieldIndex: null,
        isMovingSection: false,
        isMovingField: false,
        sourceId: null,
      });
      draggedItemRef.current = null;
    }
  };

  const handleRemoveSection = async (sectionId) => {
    setSectionMenuOpen(null);
    const ok = await confirmDelete(
      "Deleting this section will remove it from all matters created with this template."
    );
    if (!ok) return;
    const sectionToRemove = sections.find((s) => s?.id === sectionId);
    if (sectionToRemove?.isEditableSection === false) {
      toast.error("Default sections cannot be removed");
      return;
    }
    setSections((prevSections) => {
      if (sectionToRemove) {
        setUnUsedFieldsTypes((prev) => {
          const existing = new Set(prev.map((f) => f.id));
          const newFields = sectionToRemove.fields.filter(
            (f) => !existing.has(f.id)
          );
          return [
            ...prev,
            ...newFields.map((f) => ({ ...f, isNew: false, isRemoved: true })),
          ];
        });
      }
      return prevSections?.filter((section) => section?.id !== sectionId);
    });
  };

  const handleRemoveField = async (sectionId, fieldId) => {
    const ok = await confirmDelete(
      "Deleting this field will remove it from all matters created with this template."
    );
    if (!ok) {
      return false;
    }

    setActiveFieldId(null);
    setActiveSectionId(null);
    const fieldToRemove = sections
      ?.find((section) => section?.id === sectionId)
      ?.fields?.find((field) => field?.id === fieldId);

    if (fieldToRemove) {
      setUnUsedFieldsTypes?.((prev) => {
        const exists = new Set(prev.map((f) => f.id));
        if (exists.has(fieldToRemove.id)) return prev;
        return [...prev, { ...fieldToRemove, isNew: false, isRemoved: true }];
      });
    }

    // Update sections
    setSections?.((prevSections) =>
      prevSections?.map((section) => {
        if (section?.id === sectionId) {
          const updatedFields = section?.fields
            ?.filter((field) => field?.id !== fieldId)
            ?.map((field, idx) => ({
              ...field,
              position_index: idx,
            }));

          return {
            ...section,
            fields: updatedFields,
          };
        }
        return section;
      })
    );

    // Update defaultSectionFields if needed
    if (sectionId === defaultSectionId) {
      setDefaultSectionFields?.((prev) => {
        const updatedFields = prev
          ?.filter((field) => field?.id !== fieldId)
          ?.map((field, idx) => ({
            ...field,
            position_index: idx,
          }));
        return updatedFields;
      });
    }

    return true;
  };

  const handleUpdateSectionName = (sectionId, newName) => {
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId ? { ...section, name: newName } : section
      )
    );
  };

  const handleUpdateFieldLabel = (sectionId, fieldId, newLabel) => {
    // Find the field to update
    const section = sections.find((s) => s.id === sectionId);
    const field = section?.fields.find((f) => f.id === fieldId);
    if (!field) return;

    const normalizedLabel = toSnakeCase(newLabel);
    if (field.name !== "currency" && normalizedLabel === "currency") {
      toast.error(
        "The name 'currency' is reserved for the system currency field. Please choose another name."
      );
      return;
    }

    const newName = generateUniqueName(newLabel, sections, fieldId);
    // Update sections - update ONLY the specific field
    setSections((prevSections) =>
      prevSections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          // Only update the exact field with matching ID
          if (field.id === fieldId) {
            return {
              ...field,
              label: newLabel,
              name: newName,
            };
          }
          return field;
        }),
      }))
    );

    // Update defaultSectionFields - only if the field is in the default section
    if (sectionId === defaultSectionId) {
      setDefaultSectionFields((prev) =>
        prev.map((field) =>
          field.id === fieldId
            ? { ...field, label: newLabel, name: newName }
            : field
        )
      );
    }

    // Update usedFieldsTypes to maintain consistency - only update the specific field
    setUsedFieldsTypes((prev) =>
      prev.map((field) =>
        field.id === fieldId
          ? { ...field, label: newLabel, name: newName }
          : field
      )
    );
  };

  const handleUpdateSectionColumns = (sectionId, columns) => {
    setActiveColumn(columns);
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId ? { ...section, columns: columns } : section
      )
    );
    setSectionMenuOpen(null);
  };

  const handleUpdateSectionAlignment = (sectionId, alignment) => {
    setActiveAlign(alignment);
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId
          ? { ...section, alignment: alignment }
          : section
      )
    );
    setSectionMenuOpen(null);
  };

  const handleSectionMenuOpen = (e, sectionId) => {
    e.stopPropagation();
    setActiveSectionId(sectionId);
    setSectionMenuOpen(e.currentTarget);
    setSectionMenuOpenId(sectionId);

    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      setActiveColumn(section.columns || 3);
      setActiveAlign(section.alignment || "top_to_bottom");
    }
  };

  const handleFieldMenuOpen = (e, sectionId, fieldId) => {
    e.stopPropagation();
    e.preventDefault();

    if (
      sectionId === undefined ||
      sectionId === null ||
      fieldId === undefined ||
      fieldId === null
    ) {
      console.error("Invalid sectionId or fieldId:", { sectionId, fieldId });
      return;
    }

    const section = sections.find((s) => s.id === sectionId);
    if (!section) {
      console.error("Section not found:", sectionId);
      return;
    }

    const field = section.fields.find((f) => f.id === fieldId);
    if (!field) {
      console.error("Field not found:", fieldId);
      return;
    }

    setActiveSectionId(sectionId);
    setActiveFieldId(fieldId);

    const formId = `field-properties-${sectionId}-${fieldId}`;
    const fieldTitle = field?.label || "Field";

    let dialogId = "";
    const handleDialogClose = () => {
      closeDialog(dialogId);
      setActiveFieldId(null);
      setActiveSectionId(null);
    };

    dialogId = showDialog(
      <FieldMenu
        sectionId={sectionId}
        fieldId={fieldId}
        field={field}
        handleUpdateFieldLabel={handleUpdateFieldLabel}
        handleUpdateFieldProperty={handleUpdateFieldProperty}
        onClose={handleDialogClose}
        formId={formId}
      />,
      {
        dialogTitle: `${fieldTitle} | Properties`,
        width: "30rem",
        actions: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={async () => {
                const removed = await handleRemoveField(sectionId, fieldId);
                if (removed) {
                  handleDialogClose();
                }
              }}
            >
              Remove field
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="text" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                type="submit"
                form={formId}
              >
                Save changes
              </Button>
            </Box>
          </Box>
        ),
      }
    );
  };

  const getFieldWidth = (sectionColumns) => {
    switch (sectionColumns) {
      case 1:
        return "100%";
      case 2:
        return "calc(50% - 0.5rem)";
      case 3:
      default:
        return "calc(33.33% - 0.67rem)";
    }
  };

  const handleUpdateFieldProperty = (
    sectionId,
    fieldId,
    propertyName,
    value
  ) => {
    // Find the field to update
    const section = sections.find((s) => s.id === sectionId);
    const field = section?.fields.find((f) => f.id === fieldId);
    if (!field) return;

    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id !== sectionId) return section;

        return {
          ...section,
          fields: section.fields.map((field) => {
            if (field.id !== fieldId) return field;

            if (propertyName === "pickList") {
              // Create base pickList object
              const mergedPickList = {
                ...field.pickList,
                multiSelect: value.multiSelect ?? field.pickList?.multiSelect,
                useColors: value.useColors ?? field.pickList?.useColors,
                defaultValue:
                  value.defaultValue ?? field.pickList?.defaultValue,
              };

              if (value.link_option !== undefined) {
                mergedPickList.link_option = value.link_option;
                delete mergedPickList.options;
              } else if (value.options) {
                mergedPickList.options = value.options;
                delete mergedPickList.link_option;
              } else if (
                !mergedPickList.link_option &&
                !mergedPickList.options
              ) {
                toast.error("Invalid pickList configuration");
              }
              return {
                ...field,
                pickList: mergedPickList,
              };
            }

            return { ...field, [propertyName]: value };
          }),
        };
      })
    );

    // Update defaultSectionFields - only if the field is in the default section
    if (sectionId === defaultSectionId) {
      setDefaultSectionFields((prev) =>
        prev.map((field) => {
          if (field.id !== fieldId) return field;

          if (propertyName === "pickList") {
            // Same careful merging logic for defaultSectionFields
            const mergedPickList = {
              ...field.pickList,
              multiSelect: value.multiSelect ?? field.pickList?.multiSelect,
              useColors: value.useColors ?? field.pickList?.useColors,
              defaultValue: value.defaultValue ?? field.pickList?.defaultValue,
            };

            // Handle link_option and options mutually exclusively
            if (value.link_option !== undefined) {
              // If link_option is provided, use it and remove options
              mergedPickList.link_option = value.link_option;
              delete mergedPickList.options;
            } else if (value.options) {
              // If options are provided, use them and remove link_option
              mergedPickList.options = value.options;
              delete mergedPickList.link_option;
            } else if (!mergedPickList.link_option && !mergedPickList.options) {
              // Ensure there are default options if neither exists but not in currency field
              if (field.name !== "currency") {
                mergedPickList.options = [
                  { label: "Option 1", value: "option_1" },
                  { label: "Option 2", value: "option_2" },
                ];
              }
            }

            return {
              ...field,
              pickList: mergedPickList,
            };
          }

          return { ...field, [propertyName]: value };
        })
      );
    }

    // Update usedFieldsTypes to maintain consistency
    setUsedFieldsTypes((prev) =>
      prev.map((field) => {
        if (field.id !== fieldId) return field;

        if (propertyName === "pickList") {
          // Same careful merging logic for usedFieldsTypes
          const mergedPickList = {
            ...field.pickList,
            multiSelect: value.multiSelect ?? field.pickList?.multiSelect,
            useColors: value.useColors ?? field.pickList?.useColors,
            defaultValue: value.defaultValue ?? field.pickList?.defaultValue,
          };

          // Handle link_option and options mutually exclusively
          if (value.link_option !== undefined) {
            // If link_option is provided, use it and remove options
            mergedPickList.link_option = value.link_option;
            delete mergedPickList.options;
          } else if (value.options) {
            // If options are provided, use them and remove link_option
            mergedPickList.options = value.options;
            delete mergedPickList.link_option;
          } else if (!mergedPickList.link_option && !mergedPickList.options) {
            // Ensure there are default options if neither exists
            mergedPickList.options = [
              { label: "Option 1", value: "option_1" },
              { label: "Option 2", value: "option_2" },
            ];
          }

          return {
            ...field,
            pickList: mergedPickList,
          };
        }

        return { ...field, [propertyName]: value };
      })
    );
  };
  const isValidObject = (obj) => obj !== null && obj !== undefined;

  const getFormData = () => {
    // Filter out invalid sections first
    const validSections = sections.filter(isValidObject);

    // Create a reusable field mapper function to avoid code duplication
    const mapFields = (field, fieldIndex, isQuickCreate = false) => {
      if (!isValidObject(field)) return null;

      const fieldData = {
        ...field, // Preserve all existing field properties
        id: field.id || fieldIndex,
        name: field.name || "",
        type: "field",
        label: field.label || "",
        required: isQuickCreate ? true : field.required || false,
        field_type: field.value || field.field_type || "",
        isNew: true,
        position_index: field.position_index || fieldIndex, // Preserve position index
      };

      // Include tooltip if it exists
      if (field.tooltip) {
        fieldData.tooltip = field.tooltip;
        fieldData.tooltipText = field.tooltipText || "";
      } else {
        // Ensure tooltip properties are not present if not applicable
        delete fieldData.tooltip;
        delete fieldData.tooltipText;
      }

      if (
        typeof field.placeholder === "string" &&
        field.placeholder.trim() !== ""
      ) {
        fieldData.placeholder = field.placeholder.trim();
      } else {
        delete fieldData.placeholder;
      }

      // Add field-specific properties based on field type
      if (field.value === "single_line" && field.characterLimit) {
        fieldData.characterLimit = field.characterLimit;
      } else {
        delete fieldData.characterLimit;
      }

      if (field.value === "phone" && field.phoneNoCharacterLimit) {
        fieldData.phoneNoCharacterLimit = field.phoneNoCharacterLimit;
      } else {
        delete fieldData.phoneNoCharacterLimit;
      }

      // Handle pickList fields with proper error checking
      if (field.value === "pick_list" && isValidObject(field.pickList)) {
        fieldData.pickList = {
          ...field.pickList, // Preserve all pickList properties
          multiSelect: field.pickList.multiSelect || false,
          defaultValue: field.pickList.defaultValue || "",
        };

        // Handle link_option and options mutually exclusively
        if (isValidObject(field.pickList.link_option)) {
          fieldData.pickList.link_option = field.pickList.link_option;
          delete fieldData.pickList.options;
        } else if (
          Array.isArray(field.pickList.options) &&
          field.pickList.options.length > 0
        ) {
          fieldData.pickList.options = field.pickList.options
            .filter(isValidObject)
            .map((option, index) => {
              const optionValue = option.value || `option_${index + 1}`;
              const rawLabel = option.label || "";
              const defaultLabelPattern = /^option\s*\d+$/i;
              const resolvedLabel =
                !rawLabel || defaultLabelPattern.test(rawLabel)
                  ? optionValue
                  : rawLabel;

              return {
                label: resolvedLabel,
                value: optionValue,
                ...(field.pickList.useColors && option.color
                  ? { color: option.color }
                  : {}),
              };
            });
        } else {
          // Provide default options if none exist
          fieldData.pickList.options = [
            { label: "Option 1", value: "option_1" },
            { label: "Option 2", value: "option_2" },
          ];
        }

        if (field.pickList.useColors) {
          fieldData.pickList.useColors = true;
        } else {
          delete fieldData.pickList.useColors;
        }
      } else {
        delete fieldData.pickList;
      }

      // Remove temporary/deprecated properties
      delete fieldData.icon;
      delete fieldData.icons;
      // delete fieldData.isEditable;
      delete fieldData.sourceSectionId;
      delete fieldData.sourceIndex;

      return fieldData;
    };

    // Map sections for create new matter form (preserve all original section properties)
    const createNewMatterSections = validSections
      .filter((section) => section.id !== defaultSectionId)
      .map((section) => ({
        ...section, // Preserve all existing section properties
        name: section.name, // Convert display name to snake_case for backend
        fields: Array.isArray(section.fields)
          ? section.fields
              .filter(isValidObject)
              .map((field, fieldIndex) => mapFields(field, fieldIndex, false))
              .filter(isValidObject)
          : [],
      }));

    // Map sections for quick create form (preserve all original section properties)
    const quickCreateSections = validSections
      .filter((section) => section.id === defaultSectionId)
      .map((section) => ({
        ...section, // Preserve all existing section properties
        name: section.name, // Convert display name to snake_case for backend
        fields: Array.isArray(section.fields)
          ? section.fields
              .filter(isValidObject)
              .map((field, fieldIndex) => mapFields(field, fieldIndex, true))
              .filter(isValidObject)
          : [],
      }));

    const cleanFields = (field) => {
      const { icon, icons, isEditable, ...rest } = field || {};
      return {
        ...rest,
        isNew: false,
        isRemoved: field.isRemoved || false,
      };
    };

    return {
      module: "case",
      template_name: templateName || "standard",
      practice_area: practiceAreaId || "",
      linked_templated: linkId || null,
      template_description: tempDescription,
      createNewMatter: {
        form_name: "Create New Matter",
        sections: createNewMatterSections,
      },
      quickCreateNewMatter: {
        form_name: "Quick Create New Matter",
        sections: quickCreateSections,
      },
      unUsedFieldsTypes: Array.isArray(unUsedFieldsTypes)
        ? unUsedFieldsTypes.filter(isValidObject).map(cleanFields)
        : [],
      usedFieldsTypes: Array.isArray(usedFieldsTypes)
        ? usedFieldsTypes.filter(isValidObject).map(cleanFields)
        : [],
    };
  };
  const isFieldAvailableForDefaultSection = (fieldType) => {
    return !defaultSectionFields.some(
      (field) => field.value === fieldType.value
    );
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !draggedItemRef.current) return;
    };
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isDragging]);

  // Update used fields types when default section changes
  useEffect(() => {
    const defaultSection = sections.find(
      (section) => section.id === defaultSectionId
    );
    if (defaultSection) {
      // Get all field labels from default section
      const defaultSectionFieldLabels = defaultSection.fields.map(
        (field) => field.label || field.name
      );

      setUsedFieldsTypes((prev) =>
        prev.map((field) => ({
          ...field,
          disabled: defaultSectionFieldLabels.includes(
            field.label || field.name
          ),
        }))
      );
    }
  }, [sections, defaultSectionId, setUsedFieldsTypes]);

  const { setSteps, setRun } = useTour();
  const handleStartTour = () => {
    setSteps(CreatecaseTemplateFormSteps);
    setRun(true);
  };
  return (
    <>
      <Box>
        <Header
          tabBtn={tabBtn}
          templateName={templateName}
          setTemplateName={setTemplateName}
          practiceAreaId={practiceAreaId}
          setPracticeAreaId={setPracticeAreaId}
          allPracticeArea={allPracticeArea}
          handleStartTour={handleStartTour}
          placeType={"createcaseTemplateForm"}
        />
        <Box
          sx={{
            height: "calc(100vh - 2.8rem)",
            overflow: "hidden",
            display: "flex",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
            borderRadius: "8px",
          }}
        >
          <Sidebar
            tabBtn={tabBtn}
            customFieldsExpanded={customFieldsExpanded}
            unUsedExpanded={unUsedExpanded}
            usedExpanded={usedExpanded}
            toggleCustomFields={toggleCustomFields}
            toggleUnusedFields={toggleUnusedFields}
            toggleUsedFields={toggleUsedFields}
            allCustomFieldsTypes={allCustomFieldsTypes}
            unUsedFieldsTypes={unUsedFieldsTypes}
            usedFieldsTypes={usedFieldsTypes}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleSectionDragStart={handleSectionDragStart}
            setUnUsedFieldsTypes={setUnUsedFieldsTypes}
            setUsedFieldsTypes={setUsedFieldsTypes}
            templates={templateOptions}
            copyTemplateId={copyTemplateId}
            setCopyTemplateId={setCopyTemplateId}
            onCopyTemplate={handleCopyFromTemplate}
            isEditMode={isEditMode}
          />

          <FormProvider {...currentMethods}>
            <FormBuilder
              tabBtn={tabBtn}
              handleTabChange={handleTabChange}
              getFormData={getFormData}
              sections={sections}
              setSections={setSections}
              isDragging={isDragging}
              draggedItemRef={draggedItemRef}
              activeSectionDropIndex={activeSectionDropIndex}
              setActiveSectionDropIndex={setActiveSectionDropIndex}
              handleSectionDragStart={handleSectionDragStart}
              handleFieldDragStart={handleFieldDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDragLeave={handleDragLeave}
              handleSectionDrop={handleSectionDrop}
              handleSectionReorderDrop={handleSectionReorderDrop}
              handleUpdateSectionName={handleUpdateSectionName}
              handleRemoveSection={handleRemoveSection}
              handleSectionMenuOpen={handleSectionMenuOpen}
              handleFieldDrop={handleFieldDrop}
              handleFieldReorderDrop={handleFieldReorderDrop}
              handleUpdateFieldLabel={handleUpdateFieldLabel}
              handleFieldMenuOpen={handleFieldMenuOpen}
              activeFieldDropData={activeFieldDropData}
              setActiveFieldDropData={setActiveFieldDropData}
              getFieldWidth={getFieldWidth}
              dragTarget={dragTarget}
              handleUpdateFieldProperty={handleUpdateFieldProperty}
              usedFieldsTypes={usedFieldsTypes}
              setUsedFieldsTypes={setUsedFieldsTypes}
              activeSectionId={activeSectionId}
              activeFieldId={activeFieldId}
              isDefaultSection={defaultSectionId}
              isFieldAvailableForDefaultSection={
                isFieldAvailableForDefaultSection
              }
              scrollContainerRef={scrollContainerRef}
              isEditMode={isEditMode}
              editData={editTempData}
            />
          </FormProvider>
        </Box>
      </Box>

      <Menu
        id="section-menu"
        anchorEl={sectionMenuOpen}
        open={Boolean(sectionMenuOpen)}
        onClose={() => setSectionMenuOpen(null)}
        transformOrigin={{
          horizontal: "right",
          vertical: "center",
        }}
        anchorOrigin={{
          horizontal: "left",
          vertical: "center",
        }}
      >
        <SectionMenu
          activeColumn={activeColumn}
          activeAlign={activeAlign}
          handleUpdateSectionColumns={handleUpdateSectionColumns}
          handleUpdateSectionAlignment={handleUpdateSectionAlignment}
          sectionMenuOpenId={sectionMenuOpenId}
        />
      </Menu>
    </>
  );
};

export default CreatecaseTemplateForm;
